// REDESIGN 2024

import { isMobile } from '../../utils/viewport.ts';
import { handleInfoStrip } from './infoStrip.ts';
import { handleWrapperSpacings } from './navigation.ts';
import { navigationElement, infoStripElement } from './navigationElements.ts';

export const dynamicNavigation = (template: HTMLTemplateElement) => {
    // Třída pro přidání na <body> v případě aktivního stavu
    const CLASS_NAV_WITH_DYNAMIC_NAV = 'body--navigation-with-dynamic-navigation';

    // Vytvořit message
    let dynamicNav = template.content.firstElementChild?.cloneNode(true) as HTMLDivElement;
    dynamicNav.setAttribute('style', '');

    // Načíst pracovní elementy
    const brandingWrapperElement: HTMLElement | null = document.querySelector('.branding-wrapper');
    const bodyElement = document.querySelector('body');

    // Pokud <body> neobsahuje dynamickou navigaci, přidat ji z <template>
    if (!bodyElement?.contains(dynamicNav)) bodyElement?.prepend(dynamicNav);

    // Pozicovat fixně pod navigaci
    if (dynamicNav.getAttribute('style')) {
        dynamicNav.style.width = `100%`;
    }

    const gridContainer: HTMLElement | null = document.querySelector('[data-grid-container]');

    // Inicializace pomocného elementu pozadí
    addDynamicNavigationAnimatedBackground();

    // DOM element navigace (tentokrát už NE z template ale strukturu z DOMu)
    dynamicNav = document.querySelector('[data-dynamic-navigation]') as HTMLDivElement;

    // Nastavit scroll counter
    let lastScrollPosition = 0;
    const handleScroll = () => {
        const scrollTop = window.scrollY; // Aktuální pozice scrollu
        // Zjistit výšky element
        const infoStripHeight = infoStripElement ? infoStripElement.clientHeight : 0;
        const navigationHeight = navigationElement ? navigationElement.clientHeight : 0;

        let navigationTopOffset: number = scrollTop === 0 ? navigationHeight - 1 : navigationHeight - infoStripHeight - 1;

        // Zjistit směr scrollovaní
        const isScrollingDown: boolean = scrollTop > lastScrollPosition;

        if (isScrollingDown) {
            dynamicNav.style.top = `${scrollTop < 10 ? navigationTopOffset : 0}px`;
        } else {
            dynamicNav.style.top = `${navigationTopOffset + (isMobile() ? - 1 : 0)}px`;
            dynamicNav.style.transition = 'top 0.1s ease-in-out';
        }

        // Fix pro staré šablony desktopu, kde je brandingWrapperElement
        if (brandingWrapperElement && !isMobile() && !gridContainer) {
            dynamicNav.style.width = brandingWrapperElement.clientWidth <= 768 ? `100%` : `${brandingWrapperElement.clientWidth}px`;
        }
        else {
            dynamicNav.style.width = `100%`;
        }

        // Zohlednit posun 'infoStrip'
        handleInfoStrip({
            scrollTop,
            infoStripElement,
        });

        // Pokud je scroll větší než offsetTop dynamické navigace, přilepit navigaci pod navigaci
        if (!isScrollingDown && (dynamicNav.getBoundingClientRect().top <= navigationHeight - infoStripHeight)) {
            if (isMobile() || (!isMobile() && scrollTop > 10)) {
                addDynamicNavBg({
                    add: true,
                    navigationHeight,
                    infoStripHeight,
                    dynamicNav,
                });

                // Inzerce (desktop): Přidat třídu na posun brandingu
                document.body.classList.add(CLASS_NAV_WITH_DYNAMIC_NAV);
            }

        } else {

            // Inzerce (desktop): Odebrat třídu na posun brandingu
            document.body.classList.remove(CLASS_NAV_WITH_DYNAMIC_NAV); // Výchozí pozice na desktopu

            // Přidat animované pozadí shora - odstínit branding
            addDynamicNavBg({
                add: false,
                navigationHeight,
                infoStripHeight,
                dynamicNav,
            });
        }

        if (isMobile()) {
            // Mobil
            // dynamickou navigaci dat od horního okraje viewportu až pod spodní okraj navigace
            handleWrapperSpacings(navigationElement);
        } else {
            // Desktop
            handleDesktop({
                dynamicNav,
                brandingWrapperElement,
            });
        }

        // Nastavit poslední pozici scrollu při konci eventu
        lastScrollPosition = scrollTop;
    };

    // Init
    handleScroll();

    // Přidat listener na scroll
    window.addEventListener('scroll', handleScroll);
};

// Vytvořit pozadí pro dynamickou navigaci (přijede shora při scrollování)
export const addDynamicNavigationAnimatedBackground = () => {
    const dynamicNavBg = document.createElement('div');
    dynamicNavBg.setAttribute('style', '');
    dynamicNavBg.setAttribute('data-dynamic-navigation-bg', '');
    document.body.appendChild(dynamicNavBg);
};

interface HandleDesktopProps {
    dynamicNav: HTMLElement;
    brandingWrapperElement: HTMLElement | null;
}

export const handleDesktop = ({
    dynamicNav,
    brandingWrapperElement,
}: HandleDesktopProps) => {
    if (brandingWrapperElement)
        brandingWrapperElement.style.paddingTop = "0";

    // Pokud je scroll menší než offsetTop dynamické navigace, přesunout dynamickou navigaci pod navigaci a brandingWrapperElement neobsahuje dynamicNav
    if (!brandingWrapperElement?.contains(dynamicNav)) {
        brandingWrapperElement?.prepend(dynamicNav);
    }

    resizeUpToWrapper({
        dynamicNav,
        wrapperElement: brandingWrapperElement,
    });
};

// Roztáhnout podle wrapper elementu
interface ResizeUpToWrapperProps {
    dynamicNav: HTMLElement;
    wrapperElement: HTMLElement | null;
}
export const resizeUpToWrapper = ({ dynamicNav, wrapperElement }: ResizeUpToWrapperProps) => {
    if (!wrapperElement) return;

    // Pokud má brandingWrapperElement padding, nastavit odsunu pro dynamickou navigaci
    const computedStyle = window.getComputedStyle(wrapperElement);
    const paddingOfParent = computedStyle.paddingLeft;

    dynamicNav.style.paddingLeft = `-${paddingOfParent}`;
    if (parseInt(paddingOfParent) > 0) {
        dynamicNav.style.marginLeft = `-${parseInt(paddingOfParent)}px`;
    }

};

// Přidat/odebrat 'pozadí shora' pro dynamickou navigaci
interface AddDynamicNavBgProps {
    add: boolean;
    navigationHeight: number;
    infoStripHeight: number;
    dynamicNav: HTMLElement;
}
export const addDynamicNavBg = ({
    add,
    navigationHeight,
    infoStripHeight,
    dynamicNav,
}: AddDynamicNavBgProps) => {
    const dynamicNavBgElement: HTMLElement | null = document.querySelector(
        '[data-dynamic-navigation-bg]',
    );

    if (dynamicNavBgElement) {
        if (add) {
            dynamicNavBgElement.style.top = `${navigationHeight - infoStripHeight - 1}px`;
            dynamicNavBgElement.style.zIndex = 'auto';
            dynamicNavBgElement.style.height = `${dynamicNav.clientHeight}px`;
            dynamicNavBgElement.style.width = `100%`;
            dynamicNavBgElement.style.position = 'fixed';
        } else {
            dynamicNavBgElement.style.top = '0';
        }
    }
};
